import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { quotesAPI, clientsAPI, inventoryAPI, taxesAPI, accountStatusesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Edit, Download, Send, Plus, X, DollarSign, Clock, CheckCircle, XCircle, FileEdit, CreditCard, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { InvalidRoute, isValidId } from '../components/ui/page-guards';
import { format } from 'date-fns';
import { numberToWords } from '../utils/numberToWords';
import { jsPDF } from 'jspdf';
import { useCurrency } from '../contexts/CurrencyContext';
import { formatCurrency, getCurrencySymbol, formatWithSymbol } from '../utils/currency';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  accepted: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  expired: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

// Mock inventory
const INVENTORY_ITEMS = [
  { id: 1, name: 'CRM Software License', price: 999, sku: 'CRM-001' },
  { id: 2, name: 'Mobile App Integration', price: 1499, sku: 'MOB-001' },
  { id: 3, name: 'Premium Support Package', price: 299, sku: 'SUP-001' },
  { id: 4, name: 'Training Session', price: 199, sku: 'TRN-001' },
  { id: 5, name: 'Custom Integration', price: 2999, sku: 'INT-001' },
];

export default function QuoteDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const baseCurrency = useCurrency();
  
  // Route guard - validate ID before any operations
  if (!isValidId(id)) {
    return (
      <InvalidRoute
        title="Invalid Quote"
        message="The quote ID is missing or invalid. Please select a valid quote from the list."
        backPath="/quotes"
        backLabel="Back to Quotes"
      />
    );
  }
  
  const [quote, setQuote] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [lineItems, setLineItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [availableInventory, setAvailableInventory] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [quoteStatuses, setQuoteStatuses] = useState([]);

  // Document-aware currency: render in the quote's OWN snapshotted currency
  // (fallback to the user's currency for legacy quotes saved before this field).
  const docCurrencyCode = quote?.currency || baseCurrency.currencyCode || 'USD';
  const currency = {
    currencyCode: docCurrencyCode,
    symbol: getCurrencySymbol(docCurrencyCode),
    format: (amount, options = {}) => formatCurrency(amount, docCurrencyCode, options),
    formatSymbol: (amount) => formatWithSymbol(amount, docCurrencyCode),
  };
  
  // Payment state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    notes: ''
  });
  const [payments, setPayments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [linkedJob, setLinkedJob] = useState(null);

  // Check if user can update quotes
  const canUpdate = () => {
    if (!user?.module_permissions) return true; // Admin
    return hasPermission('account', 'quote', 'update');
  };

  useEffect(() => {
    loadQuote();
    loadQuoteStatuses();
  }, [id]);

  const loadQuoteStatuses = async () => {
    try {
      const statusesRes = await accountStatusesAPI.getQuoteStatuses();
      setQuoteStatuses(statusesRes.data || []);
    } catch (error) {
      console.error('Failed to load quote statuses:', error);
      toast.error('Failed to load quote statuses');
    }
  };

  const loadQuote = async () => {
    try {
      const response = await quotesAPI.getOne(id);
      const quoteData = response.data;
      setQuote(quoteData);
      
      // Load payments from backend
      try {
        const paymentsResponse = await quotesAPI.getPayments(id);
        setPayments(Array.isArray(paymentsResponse.data) ? paymentsResponse.data : []);
      } catch (error) {
        console.error('Failed to load payments:', error);
        setPayments([]);
      }
      
      // Load activities from backend, or default to "Quote created"
      if (quoteData.activities && Array.isArray(quoteData.activities) && quoteData.activities.length > 0) {
        setActivities(quoteData.activities);
      } else {
        setActivities([
          {
            id: 1,
            type: 'created',
            description: `Quote created`,
            timestamp: quoteData.created_at || new Date().toISOString()
          }
        ]);
      }

      // Load client details
      if (quoteData.client_id) {
        try {
          const clientRes = await clientsAPI.getOne(quoteData.client_id);
          setClient(clientRes.data);
        } catch (error) {
          console.error('Failed to load client:', error);
        }
      }
      
      // Load linked job details if exists (legacy support)
      if (quoteData.job_id) {
        try {
          // Jobs module removed - clear stale job_id reference
          console.log('Linked job reference found but jobs module has been removed');
        } catch (error) {
          console.error('Failed to load linked job:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load quote:', error);
      toast.error('Failed to load quote details');
      setQuote(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    // Pre-fill edit form with current quote data
    setEditForm({
      client_id: quote.client_id || '',
      quote_number: quote.quote_number || '',
      quote_date: quote.quote_date || format(new Date(), 'yyyy-MM-dd'),
      expiry_date: quote.expiry_date || '',
      status: quote.status || 'draft',
      discount_type: quote.discount_type || 'percentage',
      discount_value: quote.discount_value || 0,
      tax_rate: quote.tax_rate || 0,
      tax_name: quote.tax_name || '',
      deposit_required: quote.deposit_required || 0,
      instructions: quote.instructions || '',
      notes: quote.notes || '',
      terms: quote.terms || '',
    });
    setLineItems(quote.line_items || []);
    setInventoryItems(quote.inventory_items || []);
    
    // Load available inventory items, taxes, and quote statuses
    try {
      const [inventoryRes, taxesRes, statusesRes] = await Promise.all([
        inventoryAPI.getItems({}),
        taxesAPI.getAll(),
        accountStatusesAPI.getQuoteStatuses()
      ]);
      setAvailableInventory(inventoryRes.data || []);
      setTaxes(taxesRes.data?.items || taxesRes.data || []);
      setQuoteStatuses(statusesRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load inventory, tax, and status data');
    }
    
    setEditSheetOpen(true);
  };

  const handleUpdateQuote = async (e) => {
    e.preventDefault();
    
    const grandTotal = calculateGrandTotal();
    const quoteData = {
      ...editForm,
      line_items: lineItems,
      inventory_items: inventoryItems,
      subtotal: calculateSubtotal(),
      tax_amount: calculateTax(),
      discount_amount: calculateDiscount(),
      total_amount: grandTotal,
      total_in_words: numberToWords(grandTotal, currency.currencyCode)
    };

    try {
      await quotesAPI.update(id, quoteData);
      toast.success('Quote updated successfully');
      
      // Add activity log for edit
      const activity = {
        id: Date.now(),
        type: 'edit',
        description: `Quote updated - Total: ${currency.format(grandTotal)}`,
        timestamp: new Date().toISOString()
      };
      
      // Save activity to backend
      const updatedActivities = [...(quote.activities || []), activity];
      await quotesAPI.update(id, { activities: updatedActivities });
      
      setEditSheetOpen(false);
      loadQuote(); // Reload the quote
    } catch (error) {
      console.error('Failed to update quote:', error);
      toast.error('Failed to update quote');
    }
  };

  // Line Items Management
  const handleAddLineItem = () => {
    setLineItems([...lineItems, {
      id: Date.now(),
      description: '',
      quantity: 1,
      price: 0,
      tax: 0,
      total: 0
    }]);
  };

  const handleRemoveLineItem = (itemId) => {
    setLineItems(lineItems.filter(item => item.id !== itemId));
  };

  const handleUpdateLineItem = (itemId, field, value) => {
    setLineItems(lineItems.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };
        const subtotal = (updated.quantity || 0) * (updated.price || 0);
        updated.total = subtotal + (updated.tax || 0);
        return updated;
      }
      return item;
    }));
  };

  // Inventory Items Management
  const handleAddInventoryItem = () => {
    setInventoryItems([...inventoryItems, {
      id: Date.now(),
      itemId: '',
      quantity: 1,
      price: 0,
      taxRate: 0,
      tax: 0,
      total: 0
    }]);
  };

  const handleRemoveInventoryItem = (itemId) => {
    setInventoryItems(inventoryItems.filter(item => item.id !== itemId));
  };

  const handleUpdateInventoryItem = (itemId, field, value) => {
    setInventoryItems(inventoryItems.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };
        
        if (field === 'itemId') {
          const invItem = availableInventory.find(i => i.id === value);
          if (invItem) {
            updated.price = invItem.selling_price || invItem.price || 0;
          }
        }
        
        const subtotal = (updated.quantity || 0) * (updated.price || 0);
        updated.tax = subtotal * (updated.taxRate || 0) / 100;
        updated.total = subtotal + updated.tax;
        return updated;
      }
      return item;
    }));
  };

  // Calculate Totals
  const calculateSubtotal = () => {
    const lineItemsTotal = lineItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.price || 0)), 0);
    const inventoryTotal = inventoryItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.price || 0)), 0);
    return lineItemsTotal + inventoryTotal;
  };

  const calculateTax = () => {
    const lineItemsTax = lineItems.reduce((sum, item) => sum + (item.tax || 0), 0);
    const inventoryTax = inventoryItems.reduce((sum, item) => sum + (item.tax || 0), 0);
    const subtotal = calculateSubtotal();
    const additionalTax = subtotal * (editForm.tax_rate || 0) / 100;
    return lineItemsTax + inventoryTax + additionalTax;
  };

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    if (editForm.discount_type === 'percentage') {
      return subtotal * (editForm.discount_value || 0) / 100;
    }
    return editForm.discount_value || 0;
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax();
    const discount = calculateDiscount();
    return subtotal + tax - discount;
  };

  const handleDownloadPDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = 20;
      // jsPDF's default font can't render the ₹ glyph, so use ASCII "Rs." for INR.
      const pdfSym = currency.currencyCode === 'INR' ? 'Rs. ' : (currency.symbol || '$');
      const isINR = currency.currencyCode === 'INR';
      // Company logo (top-left). Base64 data URL is CORS-safe and synchronous; guard against bad data.
      if (user?.company_logo) {
        try {
          const props = doc.getImageProperties(user.company_logo);
          const maxH = 16, maxW = 45;
          let w = maxW, h = (props.height / props.width) * maxW;
          if (h > maxH) { h = maxH; w = (props.width / props.height) * maxH; }
          const fmt = (user.company_logo.split(';')[0].split('/')[1] || 'png').toUpperCase();
          doc.addImage(user.company_logo, fmt === 'JPG' ? 'JPEG' : fmt, margin, 10, w, h);
        } catch (e) { /* never let a bad logo break the PDF */ }
      }
      
      // Header - Center aligned
      doc.setFontSize(24);
      doc.setFont(undefined, 'bold');
      doc.text('QUOTATION', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(`Quote #${quote.quote_number || quote.id.slice(0, 8)}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 6;
      
      if (quote.quote_date) {
        doc.setFontSize(9);
        doc.text(`Quote Date: ${format(new Date(quote.quote_date), 'MMMM dd, yyyy')}`, pageWidth / 2, yPos, { align: 'center' });
        yPos += 5;
      }
      
      // Header border
      yPos += 5;
      doc.setLineWidth(1);
      doc.setDrawColor(50);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 12;
      
      // Two-column layout: From and Bill To
      const leftColumnX = margin;
      const rightColumnX = pageWidth / 2 + 5;
      const columnStartY = yPos;
      
      // From Section (Left)
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('From:', leftColumnX, yPos);
      yPos += 6;
      
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text(user?.company_name || user?.name || 'Your Company', leftColumnX, yPos);
      yPos += 5;
      
      doc.setFont(undefined, 'normal');
      const sellerAddr = (user?.company_addresses && user.company_addresses[0]) || null;
      if (sellerAddr) {
        if (sellerAddr.address_line1) { doc.text(String(sellerAddr.address_line1), leftColumnX, yPos); yPos += 4; }
        const cityLine = [sellerAddr.city, sellerAddr.state, sellerAddr.postal_code].filter(Boolean).join(', ');
        if (cityLine) { doc.text(cityLine, leftColumnX, yPos); yPos += 4; }
        if (sellerAddr.country) { doc.text(String(sellerAddr.country), leftColumnX, yPos); yPos += 4; }
      }
      if (user?.phone) { doc.text(`Phone: ${user.phone}`, leftColumnX, yPos); yPos += 4; }
      if (user?.email) { doc.text(`Email: ${user.email}`, leftColumnX, yPos); yPos += 4; }
      if (currency.currencyCode === 'INR' && user?.gstin) { doc.text(`GSTIN: ${user.gstin}`, leftColumnX, yPos); yPos += 4; }
      
      // Bill To Section (Right)
      yPos = columnStartY;
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Bill To:', rightColumnX, yPos);
      yPos += 6;
      
      if (client) {
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text(client.name, rightColumnX, yPos);
        yPos += 5;
        
        doc.setFont(undefined, 'normal');
        if (client.company_name) {
          doc.text(client.company_name, rightColumnX, yPos);
          yPos += 4;
        }
        if (client.email) {
          doc.text(client.email, rightColumnX, yPos);
          yPos += 4;
        }
        if (client.phone) {
          doc.text(client.phone, rightColumnX, yPos);
          yPos += 4;
        }
      }
      
      yPos = Math.max(yPos, columnStartY + 30);
      yPos += 12;
      
      // Order Details Section (gray box)
      const detailsBoxY = yPos;
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, yPos, pageWidth - 2 * margin, 18, 'F');
      yPos += 6;
      
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.text('Quote Date', margin + 5, yPos);
      doc.text('Valid Until', margin + 65, yPos);
      doc.text('Payment Terms', margin + 125, yPos);
      yPos += 5;
      
      doc.setFont(undefined, 'normal');
      doc.text(quote.quote_date ? format(new Date(quote.quote_date), 'MMM dd, yyyy') : '-', margin + 5, yPos);
      doc.text(quote.expiry_date ? format(new Date(quote.expiry_date), 'MMM dd, yyyy') : 'No expiry', margin + 65, yPos);
      doc.text(quote.payment_terms || 'Net 30', margin + 125, yPos);
      yPos += 12;

      // Place of Supply (statutory GST requirement on Indian tax documents)
      if (isINR && quote.place_of_supply) {
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.text('Place of Supply:', margin, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(String(quote.place_of_supply), margin + 32, yPos);
        yPos += 8;
      }
      
      // Combined Items Section
      const hasLineItems = quote.line_items && quote.line_items.length > 0;
      const hasInventory = quote.inventory_items && quote.inventory_items.length > 0;
      
      if (hasLineItems || hasInventory) {
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Quote Items', margin, yPos);
        yPos += 8;
        
        // Table header
        doc.setLineWidth(1);
        doc.setDrawColor(180);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        const colQtyX = pageWidth - 100;
        const colPriceX = pageWidth - 72;
        const colTaxX = pageWidth - 42;
        const colTotalX = pageWidth - margin - 2;
        const colHsnX = pageWidth - 128;
        doc.text('Item', margin + 2, yPos);
        if (isINR) doc.text('HSN/SAC', colHsnX, yPos, { align: 'right' });
        doc.text('Qty', colQtyX, yPos, { align: 'right' });
        doc.text('Unit Price', colPriceX, yPos, { align: 'right' });
        doc.text(isINR ? 'GST' : 'Tax', colTaxX, yPos, { align: 'right' });
        doc.text('Total', colTotalX, yPos, { align: 'right' });
        yPos += 2;
        
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
        
        // Line items
        if (hasLineItems) {
          doc.setFont(undefined, 'normal');
          quote.line_items.forEach((item) => {
            if (yPos > 260) {
              doc.addPage();
              yPos = 20;
            }
            const maxDesc = isINR ? 26 : 40;
            const description = item.description.length > maxDesc ? item.description.substring(0, maxDesc - 3) + '...' : item.description;
            doc.text(description, margin + 2, yPos);
            if (isINR) doc.text(String(item.hsn_sac || '-'), colHsnX, yPos, { align: 'right' });
            doc.text(String(item.quantity), colQtyX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.price || 0).toFixed(2)}`, colPriceX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.tax || 0).toFixed(2)}`, colTaxX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.total || 0).toFixed(2)}`, colTotalX, yPos, { align: 'right' });
            yPos += 5;
          });
        }
        
        // Inventory items
        if (hasInventory) {
          doc.setFont(undefined, 'normal');
          quote.inventory_items.forEach((item) => {
            if (yPos > 260) {
              doc.addPage();
              yPos = 20;
            }
            const itemName = (item.item_name || item.itemId || 'Unknown Item');
            const maxName = isINR ? 26 : 40;
            const displayName = itemName.length > maxName ? itemName.substring(0, maxName - 3) + '...' : itemName;
            doc.text(displayName, margin + 2, yPos);
            if (isINR) doc.text(String(item.hsn_sac || '-'), colHsnX, yPos, { align: 'right' });
            doc.text(String(item.quantity), colQtyX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.price || 0).toFixed(2)}`, colPriceX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.tax || 0).toFixed(2)}`, colTaxX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.total || 0).toFixed(2)}`, colTotalX, yPos, { align: 'right' });
            yPos += 5;
          });
        }
        
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
      }
      
      // Totals Section (right-aligned)
      if (yPos > 210) {
        doc.addPage();
        yPos = 20;
      }
      
      yPos += 5;
      const totalsX = pageWidth - 80;
      
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('Subtotal:', totalsX, yPos);
      doc.text(`${pdfSym}${(quote.subtotal || 0).toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
      yPos += 6;
      
      // GST split: CGST+SGST (intra-state) or IGST (inter-state); fall back to a single
      // Tax/GST line for legacy docs or non-GST sellers (no split stored).
      const _cgst = quote.gst_cgst || 0, _sgst = quote.gst_sgst || 0, _igst = quote.gst_igst || 0;
      if (isINR && (_cgst > 0 || _sgst > 0 || _igst > 0)) {
        if (_cgst > 0 || _sgst > 0) {
          doc.text('CGST:', totalsX, yPos);
          doc.text(`${pdfSym}${_cgst.toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
          yPos += 6;
          doc.text('SGST:', totalsX, yPos);
          doc.text(`${pdfSym}${_sgst.toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
          yPos += 6;
        }
        if (_igst > 0) {
          doc.text('IGST:', totalsX, yPos);
          doc.text(`${pdfSym}${_igst.toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
          yPos += 6;
        }
      } else {
        doc.text(isINR ? 'GST:' : 'Tax:', totalsX, yPos);
        doc.text(`${pdfSym}${(quote.tax_amount || 0).toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
        yPos += 6;
      }
      
      if (quote.discount_amount > 0) {
        doc.text('Discount:', totalsX, yPos);
        doc.text(`-${pdfSym}${(quote.discount_amount || 0).toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
        yPos += 6;
      }
      
      // Grand Total
      doc.setLineWidth(1);
      doc.setDrawColor(50);
      doc.line(totalsX - 2, yPos, pageWidth - margin, yPos);
      yPos += 7;
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('TOTAL:', totalsX, yPos);
      doc.text(`${pdfSym}${(quote.total_amount || 0).toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
      yPos += 3;
      
      // Amount in words
      const amountWords = quote.total_in_words || numberToWords(quote.total_amount || 0, currency.currencyCode);
      if (amountWords) {
        if (yPos > 250) { doc.addPage(); yPos = 20; }
        yPos += 9;
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.text('Amount in Words:', margin, yPos);
        yPos += 4;
        doc.setFont(undefined, 'normal');
        const wrappedWords = doc.splitTextToSize(String(amountWords), pageWidth - 2 * margin);
        doc.text(wrappedWords, margin, yPos);
        yPos += wrappedWords.length * 4;
      }
      
      // Required deposit
      if (quote.deposit_required > 0) {
        yPos += 4;
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text('Required Deposit:', margin, yPos);
        doc.text(`${pdfSym}${parseFloat(quote.deposit_required || 0).toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
        yPos += 5;
      }
      
      // Notes and Terms
      if (quote.notes || quote.terms) {
        yPos += 15;
        if (yPos > 240) {
          doc.addPage();
          yPos = 20;
        }
        
        if (quote.notes) {
          doc.setFontSize(8);
          doc.setFont(undefined, 'bold');
          doc.text('Notes:', margin, yPos);
          yPos += 4;
          doc.setFont(undefined, 'normal');
          const splitNotes = doc.splitTextToSize(quote.notes, pageWidth - 2 * margin);
          doc.text(splitNotes, margin, yPos);
          yPos += (splitNotes.length * 4) + 6;
        }
        
        if (quote.terms) {
          if (yPos > 240) {
            doc.addPage();
            yPos = 20;
          }
          doc.setFontSize(8);
          doc.setFont(undefined, 'bold');
          doc.text('Terms & Conditions:', margin, yPos);
          yPos += 4;
          doc.setFont(undefined, 'normal');
          const splitTerms = doc.splitTextToSize(quote.terms, pageWidth - 2 * margin);
          doc.text(splitTerms, margin, yPos);
        }
      }
      
      // Footer on all pages
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setLineWidth(0.5);
        doc.setDrawColor(180);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.text('Thank you for your business!', pageWidth / 2, pageHeight - 8, { align: 'center' });
        doc.setFontSize(6);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 2, pageHeight - 8, { align: 'right' });
      }
      
      // Save the PDF
      doc.save(`quote-${quote.quote_number || quote.id.slice(0, 8)}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleConvertToInvoice = async () => {
    if (!window.confirm('Convert this quote to an invoice? This will create a new invoice with all quote items.')) {
      return;
    }

    try {
      const response = await quotesAPI.convertToInvoice(id);
      toast.success(`Invoice ${response.data.invoice_number} created successfully!`);
      
      // Reload quote to update invoice_id field
      await loadQuote();
      
      // Navigate to the new invoice
      setTimeout(() => {
        navigate(`/invoices/${response.data.invoice_id}`);
      }, 1500);
    } catch (error) {
      console.error('Failed to convert quote to invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert quote to invoice');
    }
  };

  const handleSendForSignature = async () => {
    try {
      const res = await quotesAPI.sendForSignature(id);
      const url = res.data?.sign_url;
      if (res.data?.emailed) {
        toast.success(`Signature request sent to ${res.data.sent_to}`);
      } else if (url) {
        try { await navigator.clipboard.writeText(url); } catch {}
        toast.success('Signing link created & copied to clipboard');
      }
      await loadQuote();
    } catch (error) {
      console.error('Failed to send for signature:', error);
      toast.error(error.response?.data?.detail || 'Failed to send quote for signature');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      // Update status
      await quotesAPI.update(id, { ...quote, status: newStatus });
      setQuote({ ...quote, status: newStatus });
      
      // Add activity log
      const activity = {
        id: Date.now(),
        type: 'status_change',
        description: `Status changed from "${quote.status}" to "${newStatus}"`,
        timestamp: new Date().toISOString()
      };
      
      // Save activity to backend
      const updatedActivities = [...(quote.activities || []), activity];
      await quotesAPI.update(id, { activities: updatedActivities });
      
      // If status is 'Schedule Job', the jobs module has been removed
      // Log but skip job creation
      if (newStatus === 'Schedule Job' || newStatus === 'schedule job' || newStatus.toLowerCase() === 'schedule job') {
        console.log('Jobs module has been removed - skipping automatic job creation');
        toast.info('Status updated. Job scheduling is no longer available.');
      }
      
      // If status is 'sent', also send email to client
      if (newStatus === 'sent') {
        try {
          const response = await api.post(`/api/quotes/${id}/send-email`);
          toast.success(`Quote sent to client successfully (${response.data.sent_to})`);
        } catch (emailError) {
          console.error('Failed to send email:', emailError);
          const detail = emailError.response?.data?.detail;
          toast.error(`Status updated, but email failed: ${typeof detail === 'string' ? detail : (detail?.message || 'Unknown error')}`);
        }
      } else {
        toast.success(`Quote marked as ${newStatus}`);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    // Validate payment amount
    const paidAmount = quote.paid_amount || 0;
    const totalAmount = quote.total_amount || 0;
    const remaining = totalAmount - paidAmount;
    
    if (parseFloat(paymentForm.amount) > remaining) {
      toast.error(`Payment amount (${currency.format(parseFloat(paymentForm.amount) || 0)}) exceeds remaining balance (${currency.format(remaining)})`);
      return;
    }
    
    if (parseFloat(paymentForm.amount) <= 0) {
      toast.error('Payment amount must be greater than 0');
      return;
    }
    
    try {
      // Call backend API to add payment
      await quotesAPI.addPayment(id, {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        payment_date: paymentForm.payment_date,
        notes: paymentForm.notes
      });
      
      // Add activity log for payment
      const activity = {
        id: Date.now(),
        type: 'payment',
        description: `Payment of ${currency.format(parseFloat(paymentForm.amount) || 0)} received via ${paymentForm.payment_method}`,
        timestamp: new Date().toISOString()
      };
      
      // Reload quote first to get latest data
      await loadQuote();
      
      // Save activity to backend
      const updatedActivities = [...(quote.activities || []), activity];
      await quotesAPI.update(id, { activities: updatedActivities });
      
      toast.success('Payment added successfully');
      setPaymentDialogOpen(false);
      
      // Reset form
      setPaymentForm({
        amount: '',
        payment_method: 'cash',
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        notes: ''
      });
    } catch (error) {
      console.error('Failed to add payment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to add payment';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="quote-details-loading">
        <div className="h-8 w-64 bg-muted animate-pulse rounded"></div>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 h-96 bg-muted animate-pulse rounded"></div>
          <div className="h-96 bg-muted animate-pulse rounded"></div>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="space-y-4" data-testid="quote-not-found">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/quotes')} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Quotes
          </Button>
        </div>
        <Card className="p-8 text-center">
          <CardContent>
            <h2 className="text-2xl font-bold mb-2">Quote Not Found</h2>
            <p className="text-muted-foreground mb-4">
              The quote you&apos;re looking for doesn&apos;t exist or you don&apos;t have permission to view it.
            </p>
            <Button onClick={() => navigate('/quotes')}>
              Go Back to Quotes
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const subtotal = quote?.subtotal || 0;
  const taxAmount = quote?.tax_amount || 0;
  const discountAmount = quote?.discount_amount || 0;
  const totalAmount = quote?.total_amount || 0;
  const displayLineItems = quote?.line_items || [];
  const displayInventoryItems = quote?.inventory_items || [];

  return (
    <div className="space-y-6" data-testid="quote-details-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/quotes')} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Quotes
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Quote #{quote.quote_number || quote.id.slice(0, 8)}
            </h1>
            <p className="text-muted-foreground">
              Created on {quote.quote_date ? format(new Date(quote.quote_date), 'MMMM dd, yyyy') : '-'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge 
            style={{
              backgroundColor: quoteStatuses.find(s => s.name.toLowerCase() === quote.status?.toLowerCase())?.color || '#gray',
              color: '#fff'
            }}
            data-testid="quote-status"
          >
            {quoteStatuses.find(s => s.name.toLowerCase() === quote.status?.toLowerCase())?.name || 
             quote.status?.charAt(0).toUpperCase() + quote.status?.slice(1)}
          </Badge>
          {canUpdate() && quote.status?.toLowerCase() !== 'schedule job' && (
            <Button variant="outline" size="sm" onClick={handleEdit} data-testid="edit-quote-button">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          {quote.status?.toLowerCase() === 'schedule job' && (
            <Badge variant="secondary" className="text-xs">
              Job Scheduled - Quote Locked
            </Badge>
          )}
          {quote.esign_status === 'signed' && (
            <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-700 border-0" data-testid="quote-signed-badge">
              ✍️ Signed by {quote.esign_signer_name || 'client'}
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="download-quote-button">
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
          {quote.status === 'draft' && (
            <Button size="sm" onClick={() => handleStatusChange('sent')} data-testid="send-quote-button">
              <Send className="h-4 w-4 mr-2" />
              Send to Client
            </Button>
          )}
          {hasPermission('account', 'payment', 'create') && (quote.status === 'accepted' || quote.status === 'sent') && (() => {
            const totalPaid = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
            const amountDue = quote.deposit_required > 0 ? parseFloat(quote.deposit_required) : totalAmount;
            const remaining = Math.max(0, amountDue - totalPaid);
            return remaining > 0;
          })() && (
            <Button size="sm" onClick={() => setPaymentDialogOpen(true)} data-testid="add-payment-button">
              <CreditCard className="h-4 w-4 mr-2" />
              Add Payment
            </Button>
          )}
          {canUpdate() && !quote.invoice_id && quote.status?.toLowerCase() !== 'accepted' && (
            <Button size="sm" variant="outline" onClick={handleSendForSignature} data-testid="send-for-signature-button">
              <Send className="h-4 w-4 mr-2" />
              Send for Signature
            </Button>
          )}
          {canUpdate() && !quote.invoice_id && (
            <Button size="sm" variant="default" onClick={handleConvertToInvoice} data-testid="convert-to-invoice-button">
              <FileText className="h-4 w-4 mr-2" />
              Convert to Invoice
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Client Information</CardTitle>
            </CardHeader>
            <CardContent>
              {client ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{client.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{client.email}</p>
                  </div>
                  {client.phone && (
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium">{client.phone}</p>
                    </div>
                  )}
                  {client.company_name && (
                    <div>
                      <p className="text-sm text-muted-foreground">Company</p>
                      <p className="font-medium">{client.company_name}</p>
                    </div>
                  )}
                  {(client.address || client.city || client.state || client.zipcode || client.zip_code || client.country) && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Address</p>
                      <p className="font-medium">
                        {[
                          client.address,
                          client.city,
                          client.state,
                          client.zipcode || client.zip_code,
                          client.country
                        ].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">Client ID: {quote.client_id}</p>
              )}
            </CardContent>
          </Card>

          {displayLineItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Line Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Description</th>
                        <th className="text-right p-3 text-sm font-medium w-20">Qty</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Price</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Tax</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayLineItems.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-3">{item.description}</td>
                          <td className="p-3 text-right">{item.quantity}</td>
                          <td className="p-3 text-right">{currency.format(parseFloat(item.price || 0))}</td>
                          <td className="p-3 text-right">{currency.format(parseFloat(item.tax || 0))}</td>
                          <td className="p-3 text-right font-medium">{currency.format(parseFloat(item.total || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {displayInventoryItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Inventory Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Product</th>
                        <th className="text-right p-3 text-sm font-medium w-20">Qty</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Price</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Tax</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayInventoryItems.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-3">{item.item_name || item.itemId || 'Unknown Item'}</td>
                          <td className="p-3 text-right">{item.quantity}</td>
                          <td className="p-3 text-right">{currency.format(parseFloat(item.price || 0))}</td>
                          <td className="p-3 text-right">{currency.format(parseFloat(item.tax || 0))}</td>
                          <td className="p-3 text-right font-medium">{currency.format(parseFloat(item.total || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {quote.instructions && (
            <Card>
              <CardHeader>
                <CardTitle>Instructions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{quote.instructions}</p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-4">
            {quote.terms && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Terms & Conditions</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{quote.terms}</p>
                </CardContent>
              </Card>
            )}
            {quote.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Internal Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap text-muted-foreground">{quote.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
          
          {activities.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {activities.slice(-5).reverse().map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <div className="flex-1">
                      <p className="text-sm">{activity.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(activity.timestamp), 'MMM dd, yyyy HH:mm')}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {quote.job_id && (
            <Card>
              <CardHeader>
                <CardTitle>Linked Job</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="flex items-center gap-2 p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors"
                  onClick={() => window.open(`/jobs/${quote.job_id}`, '_blank')}
                >
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-600">
                    {linkedJob ? `Job #${linkedJob.job_number || linkedJob.id.slice(0, 8)}` : `Job #${quote.job_id.slice(0, 8)}`}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
          
          {quote.invoice_id && (
            <Card>
              <CardHeader>
                <CardTitle>Linked Invoice</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="flex items-center gap-2 p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors"
                  onClick={() => window.open(`/invoices/${quote.invoice_id}`, '_blank')}
                >
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-600">
                    Invoice #{quote.invoice_id.slice(0, 8)}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
          
          <Card>
            <CardHeader>
              <CardTitle>Quote Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Quote Date</p>
                <p className="font-medium">
                  {quote.quote_date ? format(new Date(quote.quote_date), 'MMM dd, yyyy') : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expiry Date</p>
                <p className="font-medium">
                  {quote.expiry_date ? format(new Date(quote.expiry_date), 'MMM dd, yyyy') : 'No expiry'}
                </p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Change Status</Label>
                <Select 
                  value={quote.status?.toLowerCase()} 
                  onValueChange={handleStatusChange}
                  disabled={quote.status?.toLowerCase() === 'schedule job'}
                >
                  <SelectTrigger className="mt-1" data-testid="status-selector">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {quoteStatuses.map((status) => (
                      <SelectItem key={status.id} value={status.name.toLowerCase()}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: status.color }}></div>
                          <span>{status.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {quote.status?.toLowerCase() === 'schedule job' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Status cannot be changed after job is scheduled
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Financial Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="font-medium">{currency.format(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">
                  Tax {quote.tax_name ? `(${quote.tax_name})` : ''}
                </span>
                <span className="font-medium">{currency.format(taxAmount)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Discount</span>
                  <span className="font-medium text-green-600">-{currency.format(discountAmount)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold">Total Amount</span>
                <span className="text-2xl font-bold text-primary">{currency.format(totalAmount)}</span>
              </div>
              {quote.total_in_words && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground">Amount in Words:</p>
                  <p className="text-sm font-medium italic mt-1">{quote.total_in_words}</p>
                </div>
              )}
              {quote.deposit_required > 0 && (
                <>
                  <Separator />
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Required Deposit</span>
                    <span className="text-lg font-bold text-orange-600">
                      {currency.format(parseFloat(quote.deposit_required))}
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tax Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {quote.tax_name && (
                <div>
                  <p className="text-sm text-muted-foreground">Tax Type</p>
                  <p className="font-medium">{quote.tax_name}</p>
                </div>
              )}
              {quote.tax_rate > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground">Tax Rate</p>
                  <p className="font-medium">{quote.tax_rate}%</p>
                </div>
              )}
              {(!quote.tax_name || quote.tax_rate === 0) && (
                <p className="text-sm text-muted-foreground">No tax applied</p>
              )}
            </CardContent>
          </Card>

          {payments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Payments Received
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {payments && payments.length > 0 ? (
                  <>
                    {payments.map((payment) => (
                      <div key={payment.id} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                        <div>
                          <p className="font-medium">{currency.format(parseFloat(payment.amount))}</p>
                          <p className="text-sm text-muted-foreground capitalize">
                            {payment.payment_method} • {format(new Date(payment.payment_date), 'MMM dd, yyyy')}
                          </p>
                          {payment.notes && (
                            <p className="text-xs text-muted-foreground mt-1">{payment.notes}</p>
                          )}
                        </div>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </div>
                    ))}
                    <div className="pt-2 border-t">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Paid:</span>
                        <span className="font-bold text-green-600">
                          {currency.format(payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-sm text-muted-foreground">
                          {quote.deposit_required > 0 ? 'Remaining Deposit:' : 'Remaining:'}
                        </span>
                        <span className="font-bold text-orange-600">
                          {(() => {
                            const totalPaid = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
                            const amountDue = quote.deposit_required > 0 ? parseFloat(quote.deposit_required) : totalAmount;
                            return currency.format(Math.max(0, amountDue - totalPaid));
                          })()}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No payments received yet</p>
                )}
              </CardContent>
            </Card>
          )}

        </div>
      </div>

      {/* Edit Quote Sheet */}
      {quote && (
        <Sheet open={editSheetOpen} onOpenChange={setEditSheetOpen}>
          <SheetContent side="right" className="w-full sm:max-w-6xl overflow-y-auto" data-testid="edit-quote-sheet">
            <SheetHeader>
              <SheetTitle>Edit Quote #{quote.quote_number}</SheetTitle>
            </SheetHeader>
          <form onSubmit={handleUpdateQuote} className="space-y-6 mt-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label>Client Name</Label>
                    <Input
                      value={client?.name || quote.client_name || 'Loading...'}
                      disabled
                      data-testid="edit-client-name"
                    />
                  </div>
                  <div>
                    <Label>Quote Number</Label>
                    <Input
                      value={editForm.quote_number}
                      onChange={(e) => setEditForm({ ...editForm, quote_number: e.target.value })}
                      data-testid="edit-quote-number"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Quote Date *</Label>
                    <Input
                      type="date"
                      value={editForm.quote_date}
                      onChange={(e) => setEditForm({ ...editForm, quote_date: e.target.value })}
                      required
                      data-testid="edit-quote-date"
                    />
                  </div>
                  <div>
                    <Label>Expiry Date</Label>
                    <Input
                      type="date"
                      value={editForm.expiry_date}
                      onChange={(e) => setEditForm({ ...editForm, expiry_date: e.target.value })}
                      data-testid="edit-expiry-date"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={editForm.status?.toLowerCase()} 
                      onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                    >
                      <SelectTrigger data-testid="edit-status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {quoteStatuses.map((status) => (
                          <SelectItem key={status.id} value={status.name.toLowerCase()}>
                            {status.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Line Items</h3>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddLineItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Line Item
                  </Button>
                </div>
                {lineItems.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2 text-sm font-medium">Description</th>
                          <th className="text-left p-2 text-sm font-medium w-20">Qty</th>
                          <th className="text-left p-2 text-sm font-medium w-24">Price</th>
                          <th className="text-left p-2 text-sm font-medium w-24">Tax</th>
                          <th className="text-left p-2 text-sm font-medium w-24">Total</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((item) => (
                          <tr key={item.id} className="border-t">
                            <td className="p-2">
                              <Input
                                placeholder="Item description"
                                value={item.description}
                                onChange={(e) => handleUpdateLineItem(item.id, 'description', e.target.value)}
                                className="h-8"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.quantity}
                                onChange={(e) => handleUpdateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                min="0"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.price}
                                onChange={(e) => handleUpdateLineItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.tax}
                                onChange={(e) => handleUpdateLineItem(item.id, 'tax', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td className="p-2">
                              <span className="text-sm font-medium">{currency.format(item.total)}</span>
                            </td>
                            <td className="p-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveLineItem(item.id)}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4 text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Inventory Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Inventory Items</h3>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddInventoryItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Product
                  </Button>
                </div>
                {inventoryItems.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2 text-sm font-medium">Product</th>
                          <th className="text-left p-2 text-sm font-medium w-20">Qty</th>
                          <th className="text-left p-2 text-sm font-medium w-24">Price</th>
                          <th className="text-left p-2 text-sm font-medium w-20">Tax %</th>
                          <th className="text-left p-2 text-sm font-medium w-24">Total</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryItems.map((item) => (
                          <tr key={item.id} className="border-t">
                            <td className="p-2">
                              <Select
                                value={item.itemId.toString()}
                                onValueChange={(value) => handleUpdateInventoryItem(item.id, 'itemId', value)}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue placeholder="Select product" />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableInventory.map((invItem) => (
                                    <SelectItem key={invItem.id} value={invItem.id}>
                                      {invItem.name} (${invItem.selling_price || invItem.price || 0})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.quantity}
                                onChange={(e) => handleUpdateInventoryItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                min="0"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.price}
                                className="h-8"
                                disabled
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.taxRate}
                                onChange={(e) => handleUpdateInventoryItem(item.id, 'taxRate', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                min="0"
                                max="100"
                                step="0.1"
                              />
                            </td>
                            <td className="p-2">
                              <span className="text-sm font-medium">{currency.format(item.total)}</span>
                            </td>
                            <td className="p-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveInventoryItem(item.id)}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4 text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Taxes & Discounts */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Taxes & Discounts</h3>

                <div>
                  <Label>Tax</Label>
                  <Select 
                    value={editForm.tax_rate?.toString() || "0"} 
                    onValueChange={(value) => {
                      const selectedTax = taxes.find(t => t.rate.toString() === value);
                      setEditForm({ 
                        ...editForm, 
                        tax_rate: parseFloat(value),
                        tax_name: selectedTax?.name || ''
                      });
                    }}
                  >
                    <SelectTrigger data-testid="tax-rate-select">
                      <SelectValue placeholder="Select Tax" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No Tax (0%)</SelectItem>
                      {taxes.map((tax) => (
                        <SelectItem key={tax.id} value={tax.rate.toString()}>
                          {tax.name} ({tax.rate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Discount Type</Label>
                    <Select value={editForm.discount_type} onValueChange={(value) => setEditForm({ ...editForm, discount_type: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Discount Value</Label>
                    <Input
                      type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                      value={editForm.discount_value}
                      onChange={(e) => setEditForm({ ...editForm, discount_value: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <Label>Required Deposit ($)</Label>
                    <Input
                      type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                      value={editForm.deposit_required}
                      onChange={(e) => setEditForm({ ...editForm, deposit_required: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {/* Totals Summary */}
              {(lineItems.length > 0 || inventoryItems.length > 0) && (
                <Card>
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span className="font-medium">{currency.format(calculateSubtotal())}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax:</span>
                        <span className="font-medium">{currency.format(calculateTax())}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Discount:</span>
                        <span className="font-medium text-green-600">-{currency.format(calculateDiscount())}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t">
                        <span className="text-lg font-bold">Grand Total:</span>
                        <span className="text-lg font-bold text-primary">{currency.format(calculateGrandTotal())}</span>
                      </div>
                      <div className="pt-2 border-t">
                        <p className="text-sm text-muted-foreground">Amount in Words:</p>
                        <p className="text-sm font-medium italic">{numberToWords(calculateGrandTotal(), currency.currencyCode)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Instructions and Notes */}
              <div className="space-y-4">
                <div>
                  <Label>Instructions</Label>
                  <Textarea
                    placeholder="Special instructions..."
                    value={editForm.instructions}
                    onChange={(e) => setEditForm({ ...editForm, instructions: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Internal Notes</Label>
                    <Textarea
                      placeholder="Internal notes..."
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Terms & Conditions</Label>
                    <Textarea
                      placeholder="Terms..."
                      value={editForm.terms}
                      onChange={(e) => setEditForm({ ...editForm, terms: e.target.value })}
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditSheetOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="update-quote-button">
                  Update Quote
                </Button>
              </div>
            </form>
          </SheetContent>
        </Sheet>
      )}

      {/* Add Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md" data-testid="add-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Add Payment
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPayment} className="space-y-4">
            <div>
              <Label>Payment Amount *</Label>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={paymentForm.amount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  // Prevent multiple decimal points
                  const parts = value.split('.');
                  const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                  setPaymentForm({ ...paymentForm, amount: sanitized });
                }}
                required
                data-testid="payment-amount"
              />
            </div>
            <div>
              <Label>Payment Method *</Label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
              >
                <SelectTrigger data-testid="payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Date *</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                required
                data-testid="payment-date"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                placeholder="Payment notes or reference..."
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                rows={2}
                data-testid="payment-notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="add-payment-submit">
                Add Payment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
